<template>
    <div :class="['cb-root', { 'cb-dark': isDarkTheme }]">
        <button v-if="!open" class="cb-fab" @click="openChat" :title="$t('AI Assistant')">
            <IconItem icon="mdi:message-text" height="1.4em" />
        </button>

        <div v-if="open" class="cb-panel" ref="panelEl" :style="panelStyle">
            <div class="cb-header" @mousedown.prevent="onDragStart">
                <div class="cb-header-title">
                    <IconItem icon="mdi:leaf" height="1em" />
                    <span>{{ $t("AI Assistant") }}</span>
                </div>
                <div class="cb-header-actions" @mousedown.stop>
                    <button class="cb-hbtn" :title="$t('Clear chat')" :disabled="busyDelete" @click="clearChat">
                        <IconItem icon="mdi:delete-sweep-outline" height="0.95em" />
                    </button>
                    <button class="cb-hbtn" :title="$t('Close')" @click="closeChat">
                        <IconItem icon="mdi:close" height="0.95em" />
                    </button>
                </div>
            </div>

            <div class="cb-messages" ref="msgsEl">
                <div v-if="messages.length === 0" class="cb-welcome">
                    <IconItem icon="mdi:leaf" height="2.5rem" class="cb-welcome-icon" />
                    <p>
                        {{
                            $t(
                                "Hello! I'm TerraScribe's AI assistant. I can help you consult environmental regulations, answer questions about your EIA report, and also suggest or edit content in your already generated sections.",
                            )
                        }}
                    </p>
                </div>
                <template v-else>
                    <div v-if="hasMore" class="messages-bottom-loader">
                        <IconItem icon="mdi:loading" height="1.2em" class="spin" />
                    </div>
                    <div v-for="msg in messages" :key="msg.id" :class="['cb-msg', msg.isBot ? 'cb-msg-bot' : 'cb-msg-user']">
                        <div class="cb-msg-bubble">
                            <div class="cb-msg-text" v-text="msg.message"></div>
                            <div class="cb-msg-time">{{ renderDateTime(msg.created) }}</div>
                        </div>
                    </div>
                    <div v-if="busySend" class="cb-msg cb-msg-bot">
                        <div class="cb-msg-bubble">
                            <div class="cb-typing"><span></span><span></span><span></span></div>
                            <div class="cb-typing-label">{{ $t("Writing the answer…") }}</div>
                        </div>
                    </div>
                </template>
            </div>

            <div v-if="editContext" class="cb-section-ctx">
                <IconItem icon="mdi:pencil-outline" height="0.85em" class="cb-ctx-icon" />
                <span class="cb-ctx-label"
                    >{{ $t("Editing:") }} <strong>{{ editContext.sectionTitle }}</strong></span
                >
                <button class="cb-ctx-clear" :title="$t('Cancel')" @click="clearEditContext">
                    <IconItem icon="mdi:close" height="0.8em" />
                </button>
            </div>

            <div v-if="error" class="cb-error-bar">{{ error }}</div>

            <div class="cb-input-row">
                <textarea
                    ref="inputEl"
                    class="cb-textarea"
                    v-model="inputText"
                    :placeholder="$t('Ask about environmental regulations…')"
                    :disabled="loading || busySend"
                    rows="1"
                    @keydown.enter.exact.prevent="sendMessage"
                    @input="autoResizeTextarea"
                ></textarea>
                <button class="cb-send-btn" :disabled="!inputText.trim() || loading || busySend" :title="$t('Send')" @click="sendMessage">
                    <IconItem icon="mdi:send" height="1em" />
                </button>
            </div>

            <div class="cb-rh cb-rh-n" @mousedown.prevent="onResizeStart($event, 'n')"></div>
            <div class="cb-rh cb-rh-s" @mousedown.prevent="onResizeStart($event, 's')"></div>
            <div class="cb-rh cb-rh-e" @mousedown.prevent="onResizeStart($event, 'e')"></div>
            <div class="cb-rh cb-rh-w" @mousedown.prevent="onResizeStart($event, 'w')"></div>
            <div class="cb-rh cb-rh-ne" @mousedown.prevent="onResizeStart($event, 'ne')"></div>
            <div class="cb-rh cb-rh-nw" @mousedown.prevent="onResizeStart($event, 'nw')"></div>
            <div class="cb-rh cb-rh-se" @mousedown.prevent="onResizeStart($event, 'se')"></div>
            <div class="cb-rh cb-rh-sw" @mousedown.prevent="onResizeStart($event, 'sw')"></div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { Request } from "@asanrom/request-browser";
import { getUniqueStringId } from "@/utils/unique-id";
import { getTheme } from "@/control/app-preferences";
import { elementInView } from "@/utils/in-view";
import { Timeouts } from "@/utils/timeout";
import { ApiChatbot } from "@/api/api-group-chatbot";
import { getLanguage } from "@/i18n";
import { AppEvents } from "@/control/app-events";

const DEFAULT_W = 360;
const DEFAULT_H = 480;
const MIN_W = 280;
const MIN_H = 300;
const MAX_W = 900;
const MAX_H = 800;

// How long the answer of the assistant is waited for before giving up, so a websocket
// message that never arrives does not leave the chat blocked
const ANSWER_TIMEOUT_MS = 180000;

export default defineComponent({
    name: "ChatBotWidget",
    setup: function () {
        return {
            requestId: getUniqueStringId(),
            loadRequestId: getUniqueStringId(),
            scrollInterval: null as ReturnType<typeof setInterval> | null,
        };
    },
    data: function () {
        return {
            hasMore: false,
            page: 1,
            totalPages: 1,
            firstLoad: true,

            busySend: false,
            answerTimeoutId: getUniqueStringId(),
            error: "",

            open: false,
            messages: [],
            inputText: "",
            loading: true,
            isDarkTheme: false,

            busyDelete: false,

            panelX: 0,
            panelY: 0,
            panelW: DEFAULT_W,
            panelH: DEFAULT_H,

            dragging: false,
            dragOffX: 0,
            dragOffY: 0,

            resizing: false,
            resizeDir: "",
            resizeStartX: 0,
            resizeStartY: 0,
            resizeStartW: 0,
            resizeStartH: 0,
            resizeStartLeft: 0,
            resizeStartTop: 0,

            language: getLanguage(),

            editContext: null as { sectionId: string; reportId: string; sectionTitle: string } | null,
        };
    },
    computed: {
        panelStyle: function (): Record<string, string> {
            return {
                left: this.panelX + "px",
                top: this.panelY + "px",
                width: this.panelW + "px",
                height: this.panelH + "px",
            };
        },
    },
    methods: {
        load: function () {
            Timeouts.Abort(this.loadRequestId);
            Request.Abort(this.loadRequestId);

            this.loading = true;

            Request.Pending(this.loadRequestId, ApiChatbot.GetChatbotMessages({ page: this.page }))
                .onSuccess((response) => {
                    this.page = response.page;
                    this.totalPages = response.totalPages;

                    if (this.page >= this.totalPages) {
                        this.hasMore = false;
                    } else {
                        this.hasMore = true;
                    }

                    const el = this.$refs.logEl as HTMLElement | undefined;
                    const prevScrollHeight = el ? el.scrollHeight : 0;

                    for (const message of response.messages) {
                        this.messages.unshift(message);
                    }

                    this.loading = false;

                    if (this.firstLoad) {
                        this.firstLoad = false;
                        this.$nextTick(this.scrollToBottom);
                    } else {
                        this.$nextTick(() => {
                            if (el) {
                                el.scrollTop += el.scrollHeight - prevScrollHeight;
                            }
                        });
                    }
                })
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        unauthorized: () => {
                            this.$requireLogin();
                        },
                        temporalError: () => {
                            Timeouts.Set(this.loadRequestId, 1500, this.load.bind(this));
                        },
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    Timeouts.Set(this.loadRequestId, 1500, this.load.bind(this));
                });
        },

        refresh: function () {
            this.page = 1;
            this.totalPages = 1;
            this.hasMore = false;
            this.firstLoad = true;
            this.messages = [];
            this.load();
        },

        checkLoadMore: function () {
            if (!this.hasMore || this.loading) return;

            const container = this.$refs.logEl as HTMLElement | undefined;
            const loader = container?.querySelector(".messages-bottom-loader");

            if (loader && elementInView(loader as HTMLElement)) {
                this.page++;
                this.load();
            }
        },

        onUpdateLanguage: function () {
            this.language = getLanguage();
        },

        openChat: function () {
            this.clearEditContext();
            this.openChatInternal();
        },

        openChatInternal: function () {
            this.panelX = Math.max(0, window.innerWidth - this.panelW - 24);
            this.panelY = Math.max(0, window.innerHeight - this.panelH - 80);
            this.open = true;
            this.$nextTick(() => {
                (this.$refs.inputEl as HTMLTextAreaElement)?.focus();
                this.scrollToBottom();
            });
        },

        closeChat: function () {
            this.open = false;
        },

        clearChat: function () {
            if (this.busyDelete) {
                return;
            }

            this.busyDelete = true;
            this.error = "";

            Request.Do(ApiChatbot.DeleteChatbotMessages())
                .onSuccess(() => {
                    this.busyDelete = false;
                    this.refresh();
                })
                .onRequestError((err, handleErr) => {
                    this.busyDelete = false;
                    handleErr(err, {
                        unauthorized: () => {
                            this.$requireLogin();
                        },
                        serverError: () => {
                            this.error = this.$t("Internal server error");
                        },
                        networkError: () => {
                            this.error = this.$t("Could not connect to the server");
                        },
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    this.busyDelete = false;
                    this.error = this.$t("An unexpected error occurred");
                });
        },

        waitForAnswer: function () {
            Timeouts.Abort(this.answerTimeoutId);

            Timeouts.Set(this.answerTimeoutId, ANSWER_TIMEOUT_MS, () => {
                if (!this.busySend) {
                    return;
                }

                this.busySend = false;
                this.error = this.$t("The assistant is taking too long to answer. Please try again.");
            });
        },

        stopWaitingForAnswer: function () {
            Timeouts.Abort(this.answerTimeoutId);
            this.busySend = false;
        },

        sendMessage: function () {
            if (this.busySend) {
                return;
            }

            const text = this.inputText.trim();
            if (!text || this.loading) return;

            this.inputText = "";
            this.$nextTick(this.autoResizeTextarea);

            this.messages.push({ id: Date.now(), message: text, isBot: false, created: Date.now() });
            this.$nextTick(this.scrollToBottom);

            this.busySend = true;
            this.error = "";

            const handleRequestError = (err: any, handleErr: any) => {
                this.stopWaitingForAnswer();
                handleErr(err, {
                    unauthorized: () => {
                        this.$requireLogin();
                    },
                    badRequestMessageTooLong: () => {
                        this.error = this.$t("Your message is too long. Please shorten it and try again.");
                    },
                    badRequestMessageRequired: () => {
                        this.error = this.$t("Message cannot be empty.");
                    },
                    badRequest: () => {
                        this.error = this.$t("Invalid message format.");
                    },
                    serverError: () => {
                        this.error = this.$t("Internal server error");
                    },
                    networkError: () => {
                        this.error = this.$t("Could not connect to the server");
                    },
                });
            };

            if (this.editContext) {
                Request.Pending(
                    this.requestId,
                    ApiChatbot.EditSection({
                        message: text,
                        sectionId: this.editContext.sectionId,
                        reportId: this.editContext.reportId,
                    }),
                )
                    .onSuccess(() => {
                        this.waitForAnswer();
                    })
                    .onRequestError((err, handleErr) => {
                        handleErr(err, {
                            unauthorized: () => {
                                this.$requireLogin();
                            },
                            badRequestMessageTooLong: () => {
                                this.error = this.$t("Your message is too long. Please shorten it and try again.");
                            },
                            badRequestMessageRequired: () => {
                                this.error = this.$t("Message cannot be empty.");
                            },
                            badRequestSectionIdRequired: () => {
                                this.error = this.$t("Invalid section.");
                            },
                            badRequestReportIdRequired: () => {
                                this.error = this.$t("Invalid report.");
                            },
                            badRequest: () => {
                                this.error = this.$t("Invalid message format.");
                            },
                            serverError: () => {
                                this.error = this.$t("Internal server error");
                            },
                            networkError: () => {
                                this.error = this.$t("Could not connect to the server");
                            },
                        });
                        this.stopWaitingForAnswer();
                    })
                    .onUnexpectedError((err) => {
                        console.error(err);
                        this.stopWaitingForAnswer();
                    });
            } else {
                Request.Pending(this.requestId, ApiChatbot.SendChatbotMessage({ message: text }))
                    .onSuccess(() => {
                        // The backend only acknowledges here: the answer is generated after
                        // and arrives through the websocket, so the indicator stays on until
                        // onChatbotResponse or onChatbotError turns it off
                        this.waitForAnswer();
                    })
                    .onRequestError(handleRequestError)
                    .onUnexpectedError((err) => {
                        console.error(err);
                        this.stopWaitingForAnswer();
                    });
            }
        },

        formatResults: function (results: any[], _query: string): string {
            if (!results || results.length === 0) {
                return this.$t(
                    "I could not find specific information about that topic in the legal corpus. Try rephrasing your question or using different keywords.",
                );
            }
            const parts: string[] = [];
            for (const r of results.slice(0, 3)) {
                const p = r.payload;
                if (!p?.text) continue;
                const source = p.titulo_ley || p.source_file || "Normativa";
                const snippet = p.text.length > 500 ? p.text.slice(0, 500) + "…" : p.text;
                parts.push(`【${source}】\n${snippet}`);
            }
            return parts.length > 0 ? parts.join("\n\n") : this.$t("No relevant information found for your query.");
        },

        renderDateTime: function (ts?: number): string {
            if (!ts) return "—";
            return new Date(ts).toLocaleString(this.language, {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        },

        scrollToBottom: function () {
            const el = this.$refs.msgsEl as HTMLElement;
            if (el) el.scrollTop = el.scrollHeight;
        },

        autoResizeTextarea: function () {
            const el = this.$refs.inputEl as HTMLTextAreaElement;
            if (!el) return;
            el.style.height = "auto";
            el.style.height = Math.min(el.scrollHeight, 120) + "px";
            this.error = "";
        },

        onDragStart: function (e: MouseEvent) {
            const panel = this.$refs.panelEl as HTMLElement;
            if (!panel) return;
            const rect = panel.getBoundingClientRect();
            this.dragging = true;
            this.dragOffX = e.clientX - rect.left;
            this.dragOffY = e.clientY - rect.top;
            document.addEventListener("mousemove", this.onDragMove);
            document.addEventListener("mouseup", this.onDragEnd);
        },

        onDragMove: function (e: MouseEvent) {
            if (!this.dragging) return;
            this.panelX = Math.max(0, Math.min(window.innerWidth - this.panelW, e.clientX - this.dragOffX));
            this.panelY = Math.max(0, Math.min(window.innerHeight - 60, e.clientY - this.dragOffY));
        },

        onDragEnd: function () {
            this.dragging = false;
            document.removeEventListener("mousemove", this.onDragMove);
            document.removeEventListener("mouseup", this.onDragEnd);
        },

        onResizeStart: function (e: MouseEvent, dir: string) {
            this.resizing = true;
            this.resizeDir = dir;
            this.resizeStartX = e.clientX;
            this.resizeStartY = e.clientY;
            this.resizeStartW = this.panelW;
            this.resizeStartH = this.panelH;
            this.resizeStartLeft = this.panelX;
            this.resizeStartTop = this.panelY;
            document.addEventListener("mousemove", this.onResizeMove);
            document.addEventListener("mouseup", this.onResizeEnd);
        },

        onResizeMove: function (e: MouseEvent) {
            if (!this.resizing) return;
            const dx = e.clientX - this.resizeStartX;
            const dy = e.clientY - this.resizeStartY;
            const dir = this.resizeDir;
            if (dir.includes("e")) {
                this.panelW = Math.min(MAX_W, Math.max(MIN_W, this.resizeStartW + dx));
            }
            if (dir.includes("w")) {
                const newW = Math.min(MAX_W, Math.max(MIN_W, this.resizeStartW - dx));
                this.panelX = this.resizeStartLeft + (this.resizeStartW - newW);
                this.panelW = newW;
            }
            if (dir.includes("s")) {
                this.panelH = Math.min(MAX_H, Math.max(MIN_H, this.resizeStartH + dy));
            }
            if (dir.includes("n")) {
                const newH = Math.min(MAX_H, Math.max(MIN_H, this.resizeStartH - dy));
                this.panelY = this.resizeStartTop + (this.resizeStartH - newH);
                this.panelH = newH;
            }
        },

        onResizeEnd: function () {
            this.resizing = false;
            document.removeEventListener("mousemove", this.onResizeMove);
            document.removeEventListener("mouseup", this.onResizeEnd);
        },

        clearEditContext: function () {
            this.editContext = null;
        },

        onEditSectionRequest: function (ctx: { sectionId: string; reportId: string; sectionTitle: string }) {
            this.editContext = ctx;
            if (!this.open) this.openChatInternal();
        },

        onSectionEdited: function (response: any) {
            this.stopWaitingForAnswer();
            const title = this.editContext?.sectionTitle || response.sectionTitle || "";
            this.messages.push({
                id: Date.now(),
                message: `✓ ${this.$t("Section edited")}: ${title}`,
                isBot: true,
                created: Date.now(),
            });
            AppEvents.Emit("section-content-updated", {
                sectionId: response.sectionId,
                content: response.content,
            });
            this.clearEditContext();
            this.$nextTick(this.scrollToBottom);
        },

        onChatbotResponse: function (response: any) {
            this.stopWaitingForAnswer();
            this.messages.push({
                id: response.id,
                message: response.message,
                isBot: true,
                created: response.created,
            });
            this.$nextTick(this.scrollToBottom);
        },

        onChatbotError: function (error: any) {
            this.stopWaitingForAnswer();
            this.error = error.message || this.$t("An unknown error occurred while processing your request.");
        },
    },
    mounted: function () {
        this.refresh();
        this.scrollInterval = setInterval(this.checkLoadMore.bind(this), 100);

        this.$listenOnAppEvent("chatbot-response", this.onChatbotResponse.bind(this));
        this.$listenOnAppEvent("chatbot-error", this.onChatbotError.bind(this));
        this.$listenOnAppEvent("section-edited", this.onSectionEdited.bind(this));
        this.$listenOnAppEvent("chatbot-edit-section", this.onEditSectionRequest.bind(this));
        this.$listenOnAppEvent("set-locale", this.onUpdateLanguage.bind(this));

        this.isDarkTheme = getTheme() === "dark";
        this.$listenOnAppEvent("theme-changed", () => {
            this.isDarkTheme = getTheme() === "dark";
        });
    },
    beforeUnmount: function () {
        Timeouts.Abort(this.answerTimeoutId);
        document.removeEventListener("mousemove", this.onDragMove);
        document.removeEventListener("mouseup", this.onDragEnd);
        document.removeEventListener("mousemove", this.onResizeMove);
        document.removeEventListener("mouseup", this.onResizeEnd);
        Request.Abort(this.requestId);

        Timeouts.Abort(this.loadRequestId);
        Request.Abort(this.loadRequestId);

        if (this.scrollInterval) {
            clearInterval(this.scrollInterval);
        }
    },
});
</script>

<style scoped>
.cb-root {
    display: contents;
    --blue-terrascribe: #059669;
    --blue-terrascribe-dark: #047857;
    --bg-color: #ffffff;
    --text-color: #2f2f2f;
    --theme-border-color: #d0d5dd;
    --grey-terrascribe: #eaedf1;
    --grey-terrascribe-dark: #666666;
    --input-bg-color: #ffffff;
    --input-border-color: #c9ced6;
}

.cb-root.cb-dark {
    --bg-color: #2f2f2f;
    --text-color: #ffffff;
    --theme-border-color: rgba(255, 255, 255, 0.2);
    --grey-terrascribe: #3e3e3e;
    --grey-terrascribe-dark: #727272;
    --input-bg-color: hsl(0, 0%, 7%);
    --input-border-color: rgba(255, 255, 255, 0.22);
}

.cb-fab {
    position: fixed;
    bottom: 1.5rem;
    right: 1.5rem;
    z-index: 9900;
    width: 3.5rem;
    height: 3.5rem;
    border-radius: 50%;
    background: var(--blue-terrascribe);
    color: white;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
    transition:
        background 0.15s,
        transform 0.15s,
        box-shadow 0.15s;
    font-size: 1.4rem;
}

.cb-fab:hover {
    background: var(--blue-terrascribe-dark);
    transform: scale(1.08);
    box-shadow: 0 6px 22px rgba(0, 0, 0, 0.32);
}

.cb-panel {
    position: fixed;
    z-index: 9901;
    background: var(--bg-color, #fff);
    border: 1px solid var(--theme-border-color);
    border-radius: 14px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-width: 280px;
    min-height: 300px;
}

.cb-header {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.65rem 0.8rem;
    background: var(--blue-terrascribe);
    color: white;
    cursor: grab;
    user-select: none;
    flex-shrink: 0;
}

.cb-header:active {
    cursor: grabbing;
}

.cb-header-title {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    flex: 1;
    font-weight: 700;
    font-size: 0.88rem;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.cb-header-actions {
    display: flex;
    align-items: center;
    gap: 0.2rem;
    flex-shrink: 0;
    cursor: default;
}

.cb-hbtn {
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.85);
    cursor: pointer;
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition:
        background 0.12s,
        color 0.12s;
}

.cb-hbtn:hover {
    background: rgba(255, 255, 255, 0.2);
    color: white;
}

.cb-messages {
    flex: 1;
    overflow-y: auto;
    padding: 0.9rem;
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
}

.cb-welcome {
    margin: auto;
    text-align: center;
    color: var(--grey-terrascribe-dark);
    padding: 1.5rem 1rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.6rem;
}

.cb-welcome-icon {
    color: var(--blue-terrascribe);
}

.cb-welcome p {
    font-size: 0.83rem;
    line-height: 1.55;
    margin: 0;
}

.cb-msg {
    display: flex;
    width: 100%;
}

.cb-msg-user {
    justify-content: flex-end;
}

.cb-msg-bot {
    justify-content: flex-start;
}

.cb-msg-bubble {
    max-width: 86%;
    border-radius: 12px;
    padding: 0.55rem 0.8rem;
    font-size: 0.82rem;
    line-height: 1.5;
}

.cb-msg-user .cb-msg-bubble {
    background: var(--blue-terrascribe);
    color: white;
    border-bottom-right-radius: 3px;
}

.cb-msg-bot .cb-msg-bubble {
    background: var(--grey-terrascribe);
    color: var(--text-color);
    border-bottom-left-radius: 3px;
}

.cb-msg-text {
    white-space: pre-wrap;
    word-break: break-word;
}

.cb-msg-time {
    font-size: 0.67rem;
    opacity: 0.6;
    margin-top: 0.22rem;
    text-align: right;
}

.cb-typing-label {
    font-size: 0.72rem;
    opacity: 0.7;
    margin-top: 0.2rem;
}

.cb-typing {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 0.15rem 0;
}

.cb-typing span {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--grey-terrascribe-dark);
    animation: cb-bounce 1.2s infinite ease-in-out;
}

.cb-typing span:nth-child(1) {
    animation-delay: 0s;
}
.cb-typing span:nth-child(2) {
    animation-delay: 0.2s;
}
.cb-typing span:nth-child(3) {
    animation-delay: 0.4s;
}

@keyframes cb-bounce {
    0%,
    60%,
    100% {
        transform: translateY(0);
    }
    30% {
        transform: translateY(-5px);
    }
}

.cb-error-bar {
    padding: 0.35rem 0.8rem;
    font-size: 0.78rem;
    color: #dc2626;
    background: rgba(220, 38, 38, 0.08);
    border-top: 1px solid rgba(220, 38, 38, 0.2);
    flex-shrink: 0;
}

.cb-root.cb-dark .cb-error-bar {
    color: #f87171;
    background: rgba(248, 113, 113, 0.1);
    border-top-color: rgba(248, 113, 113, 0.2);
}

.cb-input-row {
    display: flex;
    align-items: flex-end;
    gap: 0.5rem;
    padding: 0.65rem 0.8rem;
    border-top: 1px solid var(--theme-border-color);
    flex-shrink: 0;
}

.cb-textarea {
    flex: 1;
    resize: none;
    border: 1px solid var(--input-border-color, var(--theme-border-color));
    border-radius: 8px;
    padding: 0.5rem 0.65rem;
    font-size: 0.82rem;
    line-height: 1.45;
    background: var(--input-bg-color, var(--bg-color));
    color: var(--text-color);
    outline: none;
    min-height: 38px;
    max-height: 120px;
    transition: border-color 0.15s;
    font-family: inherit;
    overflow-y: auto;
}

.cb-textarea:focus {
    border-color: var(--blue-terrascribe);
}

.cb-textarea:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.cb-send-btn {
    width: 2.2rem;
    height: 2.2rem;
    border-radius: 8px;
    background: var(--blue-terrascribe);
    border: none;
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background 0.15s;
}

.cb-send-btn:hover:not(:disabled) {
    background: var(--blue-terrascribe-dark);
}

.cb-send-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.cb-rh {
    position: absolute;
    z-index: 10;
}

.cb-rh-n {
    top: 0;
    left: 12px;
    right: 12px;
    height: 5px;
    cursor: ns-resize;
}
.cb-rh-s {
    bottom: 0;
    left: 12px;
    right: 12px;
    height: 5px;
    cursor: ns-resize;
}
.cb-rh-e {
    right: 0;
    top: 12px;
    bottom: 12px;
    width: 5px;
    cursor: ew-resize;
}
.cb-rh-w {
    left: 0;
    top: 12px;
    bottom: 12px;
    width: 5px;
    cursor: ew-resize;
}

.cb-rh-ne {
    top: 0;
    right: 0;
    width: 12px;
    height: 12px;
    cursor: nesw-resize;
}
.cb-rh-nw {
    top: 0;
    left: 0;
    width: 12px;
    height: 12px;
    cursor: nwse-resize;
}
.cb-rh-sw {
    bottom: 0;
    left: 0;
    width: 12px;
    height: 12px;
    cursor: nesw-resize;
}

.cb-rh-se {
    bottom: 0;
    right: 0;
    width: 16px;
    height: 16px;
    cursor: nwse-resize;
    background: linear-gradient(
        135deg,
        transparent 40%,
        var(--theme-border-color) 40%,
        var(--theme-border-color) 55%,
        transparent 55%,
        transparent 70%,
        var(--theme-border-color) 70%
    );
    border-bottom-right-radius: 14px;
    opacity: 0.6;
}

.cb-rh-se:hover {
    opacity: 1;
}

.cb-section-ctx {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.4rem 0.8rem;
    background: rgba(5, 150, 105, 0.1);
    border-top: 1px solid rgba(5, 150, 105, 0.25);
    font-size: 0.78rem;
    color: var(--blue-terrascribe);
    flex-shrink: 0;
}

.cb-root.cb-dark .cb-section-ctx {
    background: rgba(5, 150, 105, 0.15);
    border-top-color: rgba(5, 150, 105, 0.3);
}

.cb-ctx-icon {
    flex-shrink: 0;
}

.cb-ctx-label {
    flex: 1;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.cb-ctx-clear {
    background: transparent;
    border: none;
    color: var(--blue-terrascribe);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.4rem;
    height: 1.4rem;
    border-radius: 4px;
    flex-shrink: 0;
    opacity: 0.7;
    transition: opacity 0.12s;
}

.cb-ctx-clear:hover {
    opacity: 1;
}
</style>
